<template>
  <div class="grid">
    <div class="col-12 md:col-4 border-right-1 border-300">
      <UploadPbix
        ref="uploadPbixRef"
        :loading="loadingPbix"
        :lastVersion="lastVersion"
        @add-pbix="handleAddPbix"
      />
    </div>
    <div class="col-12 md:col-8">
      <DataTable :value="pbixFiles" class="roles-table">
        <template #empty>
          <div class="p-text-center">
            <span class="p-text-bold">No hay versiones</span>
          </div>
        </template>
        <Column field="nombreFichero" header="Nombre" />
        <Column header="Fecha">
          <template #body="slotProps">
            {{ formatDate(convertUtcToLocalTime(slotProps.data.date)) }}
            {{ formatTime(convertUtcToLocalTime(slotProps.data.date)) }}
          </template>
        </Column>
        <Column field="numVersion" header="Versión">
          <template #body="slotProps">
            <div class="flex align-items-center gap-2">
              {{ slotProps.data.numVersion }}
              <Tag
                v-if="slotProps.data.active"
                value="Activa"
                severity="success"
              ></Tag>
            </div>
          </template>
        </Column>
        <Column>
          <template #body="slotProps">
            <div
              :set="(active = slotProps.data.active)"
              class="flex gap-3 justify-content-end"
            >
              <Button
                @click="handleChangeActivePbix(slotProps.data)"
                :icon="`pi ${active ? 'pi-check-circle' : 'pi-circle'}`"
                v-tooltip.top="{
                  value: active ? 'Versión activa' : 'Activar versión',
                  showDelay: 500,
                }"
                :loading="loading"
                :disabled="active"
              />
              <Button
                @click="handleDeletePbix(slotProps.data)"
                icon="pi pi-trash"
                severity="danger"
                :disabled="active"
                :loading="loading"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";

import Button from "primevue/button";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Tag from "primevue/tag";

import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";

import UploadPbix from "@/components/partials/UploadPbix.vue";

import {
  formatDate,
  formatTime,
  convertUtcToLocalTime,
} from "@/composables/_commons/helper.js";

import {
  getVersion,
  uploadPbix,
  changeActiveVersion,
  deleteVersion,
} from "@/api/pbix/pbix.api";

// #region Props, Emits
const props = defineProps({
  alias: {
    type: String,
    default: null,
  },
  guidReportPowerBI: {
    type: String,
    default: null,
  },
});
// #endregion

// #region Params, Ref, Reactives, Computed
const pbixFiles = ref([]);
const confirm = useConfirm();
const toast = useToast();
const loading = ref(false);
const uploadPbixRef = ref();
const loadingPbix = ref(false);
const lastVersion = ref(null);
// #endregion

// #region Functions
async function handleAddPbix() {
  if (uploadPbixRef.value.validatePbix() === false) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: uploadPbixRef.value.dragAndDropErrorRef,
      life: 3000,
    });

    return;
  }

  loadingPbix.value = true;
  loading.value = true;

  const file = uploadPbixRef.value.getPbixFile();
  const titulo = uploadPbixRef.value.getPbixFileName();

  const response = await uploadPbix({
    GuidPbix: props.guidReportPowerBI,
    File: file,
    TituloPbix: titulo,
  });

  loadingPbix.value = false;
  loading.value = false;
  handleGetVersions();

  if (response.status != 200) {
    const error = response.response?.data?.errors?.[0]?.message;

    if (error) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error,
        life: 3000,
      });
    } else {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Error al borrar la versión",
        life: 3000,
      });
    }
  } else {
    toast.add({
      severity: "success",
      summary: "Versión añadida",
      detail: "Se ha añadido la versión correctamente",
      life: 3000,
    });

    toast.add({
      severity: "info",
      summary: "Información",
      detail: `Los cambios pueden tardar unos minutos en reflejarse`,
    });

    uploadPbixRef.value.clearPbix();
  }
}

function handleChangeActivePbix(pbix) {
  if (pbix.active) return;

  confirm.require({
    message: "¿Desea definir esta versión como activa?",
    header: "Definir como activa",
    icon: "pi pi-exclamation-triangle",
    rejectClass: "p-button-secondary p-button-outlined",
    accept: async () => {
      loading.value = true;

      const response = await changeActiveVersion({
        VersionToActivate: pbix.idVersionInformePowerBI,
        Alias: props.alias,
      });

      loading.value = false;
      handleGetVersions();

      if (response.status != 200) {
        const error = response.response?.data?.errors?.[0]?.message;

        if (error) {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: error,
            life: 3000,
          });
        } else {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Error al definir la versión como activa",
            life: 3000,
          });
        }
      } else {
        toast.add({
          severity: "success",
          summary: "Versión activa cambiada",
          detail: `Versión definida como activa: ${pbix.numVersion}`,
          life: 3000,
        });

        toast.add({
          severity: "info",
          summary: "Información",
          detail: `Los cambios pueden tardar unos minutos en reflejarse`,
        });
      }
    },
  });
}

function handleDeletePbix(rol) {
  confirm.require({
    message: "¿Desea borrar el registro?",
    header: "Borrar",
    icon: "pi pi-exclamation-triangle",
    rejectClass: "p-button-secondary p-button-outlined",
    accept: async () => {
      loading.value = true;

      const response = await deleteVersion({
        versionToDelete: rol.idVersionInformePowerBI,
        alias: props.alias,
      });

      loading.value = false;
      handleGetVersions();

      if (response.status != 200) {
        const error = response.response?.data?.errors?.[0]?.message;

        if (error) {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: error,
            life: 3000,
          });
        } else {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Error al borrar la versión",
            life: 3000,
          });
        }
      } else {
        toast.add({
          severity: "success",
          summary: "Versión borrada",
          detail: "Se ha borrado la versión correctamente",
          life: 3000,
        });
      }
    },
  });
}

async function handleGetVersions() {
  const response = await getVersion(props.alias);

  if (response.status == 200) {
    pbixFiles.value = response.data;

    if (pbixFiles.value.length > 0) {
      lastVersion.value = pbixFiles.value.reduce(
        (max, pbix) =>
          pbix.idVersionInformePowerBI > max.idVersionInformePowerBI
            ? pbix
            : max,
        pbixFiles.value[0]
      );
    }
  } else {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Error al obtener las versiones",
      life: 3000,
    });
  }
}
// #endregion

// #region Effects, Hooks
onMounted(() => {
  handleGetVersions();
});
// #endregion

// #region Provide, Expose
// #endregion
</script>
